/**
 * W4 Hop B (N29) — the import's arrival-notice column, cell by cell.
 *
 * A cell names the addresses that get an email when THIS row's guest
 * arrives: split on `;`, `,` or whitespace, each part cleaned and checked
 * like the e-mail column (`DataTypeDetectionService`), at most
 * ARRIVAL_NOTICE_MAX_PER_CELL distinct addresses (the backend's
 * contacts-per-guest cap). A cell with ONE bad address or too many is
 * ignored WHOLE — half a list would link a guest to fewer people than the
 * sheet says, silently — and the checker says how many such cells there are.
 *
 * The addresses never reach the job: PImportCore turns them into contact
 * ids through `ensure` and sends `arrivalNoticeLinks` (contract §7).
 */
import { DataTypeDetectionService } from "@/lib/utils/import-dtype-detection";
import { DEFAULT_NOTIFICATION_CONTACT_CAPS } from "@/types/NotificationContact";
import { ARRIVAL_NOTICE_IMPORT_FIELD } from "@/lib/utils/import-field-utils";
import type { Field } from "@/types/Field";
import type { ImportMapper } from "../types/ImportMapper";
import type { TableColumnData } from "../types/TableColumnData";

export const ARRIVAL_NOTICE_MAX_PER_CELL = DEFAULT_NOTIFICATION_CONTACT_CAPS.contactsPerGuest;

export type ArrivalNoticeCell =
  | { kind: "empty" }
  | { kind: "ok"; emails: string[] }
  | { kind: "invalid"; reason: "address" | "tooMany" };

/** The built-in column — a CUSTOM field an admin named `arrivalNotice` is not it. */
export function isArrivalNoticeField(field: Field | undefined | null): boolean {
  return !!field && field.name === ARRIVAL_NOTICE_IMPORT_FIELD.name && field.additionalField === false;
}

/** One address as the backend keys it: cleaned like the e-mail column, lower-case. */
export function arrivalNoticeAddressKey(raw: string): string {
  return DataTypeDetectionService.cleanEmailValue(raw).toLowerCase();
}

export function parseArrivalNoticeCell(raw: unknown): ArrivalNoticeCell {
  if (typeof raw !== "string") return { kind: "empty" };
  const parts = raw.split(/[;,\s]+/).filter((part) => part !== "");
  const emails: string[] = [];
  for (const part of parts) {
    // A part that is only invisible characters is no address and no error.
    if (DataTypeDetectionService.isEmptyEmailCell(part)) continue;
    if (!DataTypeDetectionService.isValidEmailValue(part)) return { kind: "invalid", reason: "address" };
    const key = arrivalNoticeAddressKey(part);
    if (!emails.includes(key)) emails.push(key);
  }
  if (emails.length === 0) return { kind: "empty" };
  if (emails.length > ARRIVAL_NOTICE_MAX_PER_CELL) return { kind: "invalid", reason: "tooMany" };
  return { kind: "ok", emails };
}

export type ArrivalNoticeColumnSummary = {
  /** Is the built-in column mapped at all. */
  mapped: boolean;
  /** Rows whose cell names at least one valid address list. */
  rows: number;
  /** Distinct addresses across those rows. */
  addresses: number;
  /** Cells ignored whole: a bad address, or more than the cap. */
  invalidCells: number;
};

/**
 * The checker's view of the column, with the row builder's rule for a blank
 * cell (the column default stands in, as `parseGuest` does).
 */
export function summarizeArrivalNoticeColumn(
  mappingMap: Map<number, ImportMapper>,
  tableData: TableColumnData[],
): ArrivalNoticeColumnSummary {
  const summary: ArrivalNoticeColumnSummary = { mapped: false, rows: 0, addresses: 0, invalidCells: 0 };
  const seen = new Set<string>();
  for (const [index, mapper] of mappingMap.entries()) {
    if (!isArrivalNoticeField(mapper.field)) continue;
    summary.mapped = true;
    const entry = index < tableData.length ? tableData[index] : null;
    const fallback = typeof mapper.value === "string" ? mapper.value : "";
    for (const val of entry?.values ?? []) {
      const raw = typeof val === "string" && val.trim() !== "" ? val : fallback;
      const cell = parseArrivalNoticeCell(raw);
      if (cell.kind === "invalid") summary.invalidCells++;
      if (cell.kind !== "ok") continue;
      summary.rows++;
      cell.emails.forEach((email) => seen.add(email));
    }
  }
  summary.addresses = seen.size;
  return summary;
}
